import { useEffect, useRef } from "react";

// Pre-baked as [r,g,b] — avoids string parsing each frame
const COLORS = [
    [186, 61,  135],
    [82,  55,  204],
    [232, 122, 207],
    [139, 108, 240],
    [255, 255, 255],
    [201, 168, 255],
];

const MAX = 120;

function drawStar4(ctx, r) {
    const inner = r * 0.38;
    const step  = Math.PI / 4;
    let rot = -Math.PI / 2;
    ctx.beginPath();
    ctx.moveTo(Math.cos(rot) * r, Math.sin(rot) * r);
    for (let i = 0; i < 4; i++) {
        rot += step;
        ctx.lineTo(Math.cos(rot) * inner, Math.sin(rot) * inner);
        rot += step;
        ctx.lineTo(Math.cos(rot) * r,     Math.sin(rot) * r);
    }
    ctx.closePath();
}

export const CursorEffect = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx    = canvas.getContext("2d");

        let pts = [];
        let mx = -999, my = -999;
        let px = -999, py = -999;
        let raf;

        const resize = () => {
            canvas.width  = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener("resize", resize);

        const onMove = (e) => {
            px = mx; py = my;
            mx = e.clientX; my = e.clientY;
            const spd   = Math.hypot(mx - px, my - py);
            const burst = Math.min(Math.floor(spd / 7) + 1, 4);
            for (let i = 0; i < burst && pts.length < MAX; i++) spawn();
        };
        window.addEventListener("mousemove", onMove);

        function spawn() {
            const ang  = Math.random() * Math.PI * 2;
            const spd  = Math.random() * 1.6 + 0.3;
            const col  = COLORS[Math.floor(Math.random() * COLORS.length)];
            const star = Math.random() > 0.4;
            pts.push({
                x: mx + (Math.random() - 0.5) * 6,
                y: my + (Math.random() - 0.5) * 6,
                vx:   Math.cos(ang) * spd,
                vy:   Math.sin(ang) * spd - 0.7,
                life: 1,
                dec:  Math.random() * 0.008 + 0.004,
                sz:   star ? Math.random() * 3 + 1.5 : Math.random() * 1.8 + 0.5,
                r: col[0], g: col[1], b: col[2],
                wave: Math.random() * Math.PI * 2,
                wSpd: (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 0.05 + 0.02),
                wAmp: Math.random() * 1.4 + 0.5,
                star,
                twk:  Math.random() * Math.PI * 2,
                tSpd: Math.random() * 0.1 + 0.05,
                rot:  Math.random() * Math.PI * 2,
                rSpd: (Math.random() - 0.5) * 0.05,
            });
        }

        function draw() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Constellation lines — single batched path, fixed style (no per-line strokeStyle)
            const sub = pts.length > 50 ? pts.slice(-50) : pts;
            ctx.strokeStyle = "rgba(186,61,135,0.18)";
            ctx.lineWidth   = 0.5;
            ctx.beginPath();
            for (let i = 0; i < sub.length; i++) {
                for (let j = i + 1; j < sub.length; j++) {
                    const dx = sub[i].x - sub[j].x;
                    const dy = sub[i].y - sub[j].y;
                    if (dx * dx + dy * dy < 7225) { // 85²
                        ctx.moveTo(sub[i].x, sub[i].y);
                        ctx.lineTo(sub[j].x, sub[j].y);
                    }
                }
            }
            ctx.stroke();

            // Additive blend = GPU-composited glow, zero shadowBlur needed
            ctx.globalCompositeOperation = "lighter";

            const next = [];
            for (let i = 0; i < pts.length; i++) {
                const p = pts[i];

                p.wave += p.wSpd;
                p.x    += p.vx + Math.sin(p.wave) * p.wAmp;
                p.y    += p.vy + Math.cos(p.wave * 0.65) * p.wAmp * 0.4;
                p.vy   += 0.018;
                p.vx   *= 0.997;
                p.vy   *= 0.997;
                p.life -= p.dec;
                p.twk  += p.tSpd;
                p.rot  += p.rSpd;

                if (p.life < 0.015) continue;
                next.push(p);

                const twk = 0.6 + 0.4 * Math.sin(p.twk);
                const a   = p.life * twk;
                const sz  = p.sz * (0.35 + 0.65 * Math.min(p.life * 2.5, 1));
                const { r, g, b } = p;

                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rot);

                if (p.star) {
                    // Faint outer glow halo (additive blend makes it bloom naturally)
                    ctx.fillStyle = `rgba(${r},${g},${b},${(a * 0.07).toFixed(3)})`;
                    drawStar4(ctx, sz * 5);
                    ctx.fill();
                    // Core star
                    ctx.fillStyle = `rgba(${r},${g},${b},${(a * 0.88).toFixed(3)})`;
                    drawStar4(ctx, sz * 1.7);
                    ctx.fill();
                    // White-hot centre
                    ctx.fillStyle = `rgba(255,255,255,${(a * 0.7).toFixed(3)})`;
                    ctx.beginPath();
                    ctx.arc(0, 0, sz * 0.4, 0, Math.PI * 2);
                    ctx.fill();
                } else {
                    // Glow halo
                    ctx.fillStyle = `rgba(${r},${g},${b},${(a * 0.09).toFixed(3)})`;
                    ctx.beginPath();
                    ctx.arc(0, 0, sz * 3.5, 0, Math.PI * 2);
                    ctx.fill();
                    // Core dot
                    ctx.fillStyle = `rgba(${r},${g},${b},${(a * 0.95).toFixed(3)})`;
                    ctx.beginPath();
                    ctx.arc(0, 0, sz, 0, Math.PI * 2);
                    ctx.fill();
                }

                ctx.restore();
            }

            ctx.globalCompositeOperation = "source-over";
            pts = next;
            raf = requestAnimationFrame(draw);
        }

        draw();

        return () => {
            window.removeEventListener("resize", resize);
            window.removeEventListener("mousemove", onMove);
            cancelAnimationFrame(raf);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{
                position:      "fixed",
                top:           0,
                left:          0,
                width:         "100%",
                height:        "100%",
                pointerEvents: "none",
                zIndex:        9999,
            }}
        />
    );
};
